import { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { DatePickerInput } from "@mantine/dates";
import {
    Alert,
    Flex,
    Badge,
    Button,
    Center,
    Group,
    Loader,
    MultiSelect,
    Paper,
    Stack,
    Text,
    Title,
} from "@mantine/core";
import { IconAlertCircle, IconCalendar, IconSearch } from "@tabler/icons-react";
import AuthContext from "../../../contexts/AuthContext";
import "./AcademicLessonPreview.css";
import AppContext from "../../../contexts/AppContext";

const AR_TIMEZONE = "America/Argentina/Buenos_Aires";

/**
 * DatePickerInput devuelve Date en Mantine 7 y string "YYYY-MM-DD" en Mantine 8.
 * Normalizamos y fijamos el borde del día en horario local, porque un
 * "YYYY-MM-DD" pelado lo lee el backend como medianoche UTC y la fecha
 * "hasta" se comería las clases de ese mismo día.
 */
const toRangeISO = (_value, _edge) => {
    if (!_value) return undefined;

    const date = typeof _value === "string" ? new Date(`${_value}T00:00:00`) : new Date(_value);
    if (Number.isNaN(date.getTime())) return undefined;

    if (_edge === "start") date.setHours(0, 0, 0, 0);
    else date.setHours(23, 59, 59, 999);

    return date.toISOString();
};

const shortDateFormatter = new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    timeZone: AR_TIMEZONE,
});

const timeFormatter = new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: AR_TIMEZONE,
});

const longDateFormatter = new Intl.DateTimeFormat("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: AR_TIMEZONE,
});

const getPersonName = (_person) => {
    if (!_person || typeof _person !== "object") return null;
    const fullName = `${_person.name || ""} ${_person.lastname || ""}`.trim();
    return fullName && fullName !== "N/A" ? fullName : null;
};

const MetaItem = ({ label, value }) => (
    <div>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {label}
        </Text>
        <Text size="sm">{value}</Text>
    </div>
);

const LessonCard = ({ lesson }) => {

    const initDate = lesson.initDate ? new Date(lesson.initDate) : null;
    const teachers = (lesson.teachers || []).map(getPersonName).filter(Boolean);
    const moderator = getPersonName(lesson.moderator);
    const academicUnits = (lesson.academicUnits || []).filter(Boolean);
    const isOpen = lesson?.state?.toUpperCase() === 'OPEN'

    return (
        <Paper withBorder radius="md" className="academicLessons__card">
            <div className={`academicLessons__date ${!isOpen ? "academicLessons__date--closed" : ""}`}>
                <p className="academicLessons__lessonLabel">Clase</p>
                <Text className="academicLessons__dateValue">
                    {initDate ? shortDateFormatter.format(initDate) : "--/--"}
                </Text>
                {initDate && <p style={{fontSize:"var(--fs-body)"}}>{timeFormatter.format(initDate)}</p>}
            </div>

            <Stack gap={10} p="md" style={{ flex: 1, minWidth: 0 }}>
                <div>
                    <Group gap="xs" align="center">
                        <h3 style={{ fontSize: 'var(--fs-body)' }} fw={500} tt="uppercase" lh={1.4}>
                            {lesson.name}
                        </h3>
                        {lesson.state && (
                            <Badge variant="light" color={isOpen ? "var(--color-secondary)" : "var(--color-badge-red)"} size="sm">
                                {isOpen ? "Abierta" : "Cerrada"}
                            </Badge>
                        )}
                    </Group>

                    {initDate && (
                        <Text size="sm" c="dimmed" tt="capitalize">
                            {longDateFormatter.format(initDate)}
                        </Text>
                    )}
                </div>

                <Group gap="xl" align="flex-start">
                    {academicUnits.length > 0 && (
                        <MetaItem label="Unidad académica" value={academicUnits.join(" · ")} />
                    )}
                    {teachers.length > 0 && (
                        <MetaItem
                            label={teachers.length > 1 ? "Profesores" : "Profesor"}
                            value={teachers.join(", ")}
                        />
                    )}
                    {moderator && <MetaItem label="Moderador" value={moderator} />}
                    {lesson?.roomUrl && <MetaItem label="Link de zoom" value={lesson?.roomUrl} />}
                </Group>
            </Stack>


        </Paper>
    );
};

export default function AcademicLessonPreview() {
    const { authToken } = useContext(AuthContext);
    const { academicUnits: academicUnitContext } = useContext(AppContext);
    const [dateRange, setDateRange] = useState([null, null]);
    const [acaUnits, setAcaUnits] = useState([]);
    const [state, setState] = useState({ isLoading: false, error: "", data: null });

    // El backend exige las dos fechas: con una sola cae al rango por defecto
    // sin avisar, así que lo bloqueamos acá.
    const hasPartialRange = Boolean(dateRange[0]) !== Boolean(dateRange[1]);

    const fetchLessons = useCallback(
        async ({ range = [null, null], acaUnits = [] } = {}, signal) => {
            setState((prev) => ({ ...prev, isLoading: true, error: "" }));

            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/allLessons`,
                    {
                        params: {
                            startDate: toRangeISO(range[0], "start"),
                            endDate: toRangeISO(range[1], "end"),
                            acaUnit: acaUnits.length ? acaUnits : undefined,
                        },
                        // "repeat" manda acaUnit=a&acaUnit=b en vez de acaUnit[]=a
                        // paramsSerializer: { indexes: null },
                        paramsSerializer: (params) =>
                            new URLSearchParams(
                                Object.entries(params).flatMap(([key, value]) =>
                                    value === undefined ? [] :
                                        Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]
                                )
                            ).toString(),
                        headers: { Authorization: `Bearer ${authToken}` },
                        signal,
                    }
                );

                // Sincronizamos el picker con el rango que el backend aplicó
                // de verdad, para que se vea la semana por defecto.
                if (data.range) {
                    setDateRange([new Date(data.range.startDate), new Date(data.range.endDate)]);
                }

                setState({ isLoading: false, data, error: "" });
            } catch (error) {
                if (axios.isCancel(error)) return;

                setState({
                    isLoading: false,
                    data: null,
                    error:
                        error?.response?.data?.message ||
                        "No se pudieron cargar las clases. Volvé a intentar en unos minutos.",
                });
            }
        },
        [authToken]
    );

    useEffect(() => {
        const controller = new AbortController();
        fetchLessons({}, controller.signal);
        return () => controller.abort();
    }, [fetchLessons]);

    const lessons = state.data?.lessons || [];

    return (
        <Paper withBorder radius="md" p="lg">
            <Group justify="space-between" align="baseline" mb="md">
                <Title order={4}>Clases</Title>
                {!state.isLoading && !state.error && (
                    <Text size="sm" c="dimmed">
                        {lessons.length === 1
                            ? "Mostrando 1 resultado"
                            : `Mostrando ${lessons.length} resultados`}
                    </Text>
                )}
            </Group>

            <Paper withBorder radius="sm" p="md" mb="lg">
                <Group gap="xs" mb="sm">
                    <IconCalendar size={18} />
                    <Text fw={600} size="sm">
                        Filtrar por fecha
                    </Text>
                </Group>

                <Group align="flex-end" gap="md">
                    <DatePickerInput
                        type="range"
                        label="Rango de fechas"
                        placeholder="Seleccioná un rango"
                        value={dateRange}
                        onChange={setDateRange}
                        valueFormat="DD/MM/YYYY"
                        clearable
                        maw="300px"
                        allowSingleDateInRange
                        error={hasPartialRange ? "Elegí también la fecha de cierre" : null}
                        style={{ flex: "1 1 280px" }}
                    />

                    <MultiSelect
                        label="Unidad académica"
                        placeholder={acaUnits.length ? undefined : "Todas"}
                        data={academicUnitContext || []}
                        value={acaUnits}
                        onChange={setAcaUnits}
                        searchable
                        clearable
                        style={{ flex: "1 1 280px" }}
                    />

                    <Flex
                    w='100%'>
                        <Button
                        onClick={() => fetchLessons({ range: dateRange, acaUnits })}
                        loading={state.isLoading}
                        miw='200px'
                        color='var(--color-secondary-light)'
                        disabled={hasPartialRange}
                        leftSection={<IconSearch/>}
                    >
                        Filtrar
                    </Button>
                    </Flex>
                </Group>
            </Paper>

            {state.isLoading && (
                <Center py="xl">
                    <Loader size="sm" />
                </Center>
            )}

            {!state.isLoading && state.error && (
                <Alert color="red" icon={<IconAlertCircle size={18} />} title="No se pudo cargar">
                    {state.error}
                </Alert>
            )}

            {!state.isLoading && !state.error && lessons.length === 0 && (
                <Center py="xl">
                    <Text c="dimmed" size="sm">
                        No hay clases en este rango. Probá con otras fechas.
                    </Text>
                </Center>
            )}

            {!state.isLoading && !state.error && lessons.length > 0 && (
                <Stack gap="sm">
                    {lessons.map((lesson) => (
                        <LessonCard key={lesson._id} lesson={lesson} />
                    ))}
                </Stack>
            )}
        </Paper>
    );
}